import { useEffect, useState } from "react"
import { auth, db } from "../../../services/firebase"
import {
  collection, addDoc, getDocs, doc, getDoc, setDoc
} from "firebase/firestore"
import QRCode from "qrcode"
import "./style/Deposit.scss"

type WalletsConfig = Record<string, string>

const COIN_CONFIG = {
  BTC: { name: "Bitcoin", networks: ["BTC"], priceEur: 40000 },
  ETH: { name: "Ethereum", networks: ["ERC20"], priceEur: 2500 },
  USDT: { name: "Tether", networks: ["ERC20", "TRC20"], priceEur: 1 }
} as const

type Coin = keyof typeof COIN_CONFIG

const Deposit = () => {
  const user = auth.currentUser
  const [coin, setCoin] = useState<Coin>("BTC")
  const [network, setNetwork] = useState("BTC")
  const [eurAmount, setEurAmount] = useState("")
  const [wallets, setWallets] = useState<WalletsConfig>({})
  const [qrUrl, setQrUrl] = useState("")
  const [deposits, setDeposits] = useState<any[]>([])
  const [userWallet, setUserWallet] = useState("")
  const [coinIcons, setCoinIcons] = useState<Record<string, { name: string; image: string }>>({})

  const coinPrice = COIN_CONFIG[coin].priceEur
  const coinAmount = eurAmount ? (parseFloat(eurAmount) / coinPrice).toFixed(6) : ""

  // Fetch global wallets
  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "wallets"))
      if (snap.exists()) setWallets(snap.data() as WalletsConfig)
    }
    load()
  }, [])

  // Check user wallet for selected coin-network
  useEffect(() => {
    const load = async () => {
      if (!user) return
      const key = `${coin}_${network}`
      const ref = doc(db, "users", user.uid, "wallets", key)
      const snap = await getDoc(ref)
      if (snap.exists()) {
        const address = snap.data().address
        setUserWallet(address)
        const qr = await QRCode.toDataURL(address)
        setQrUrl(qr)
      } else {
        setUserWallet("")
        setQrUrl("")
      }
    }
    load()
  }, [coin, network])

  // Load deposit history
  useEffect(() => {
    const load = async () => {
      if (!user) return
      const snap = await getDocs(collection(db, "users", user.uid, "deposits"))
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setDeposits(list)
    }
    load()
  }, [])

  const handleActivateWallet = async () => {
    if (!user) return
    const key = `${coin}_${network}`
    const globalAddress = wallets[key]
    if (!globalAddress) return alert("Wallet address not configured.")

    const ref = doc(db, "users", user.uid, "wallets", key)
    await setDoc(ref, { address: globalAddress })

    const qr = await QRCode.toDataURL(globalAddress)
    setUserWallet(globalAddress)
    setQrUrl(qr)
  }

  useEffect(() => {
  const fetchIcons = async () => {
    const res = await fetch("https://api.coingecko.com/api/v3/coins/markets?vs_currency=eur&order=market_cap_desc&per_page=100")
    const data = await res.json()

    const iconMap: Record<string, { name: string; image: string }> = {}
    data.forEach((coin: any) => {
      iconMap[coin.symbol.toUpperCase()] = {
        name: coin.name,
        image: coin.image
      }
    })

    setCoinIcons(iconMap)
  }

  fetchIcons()
}, [])


  const handleSubmit = async () => {
    if (!user || !eurAmount || !userWallet) return
    const ref = collection(db, "users", user.uid, "deposits")
    await addDoc(ref, {
      coin, network, eurAmount: parseFloat(eurAmount),
      coinAmount: parseFloat(coinAmount),
      wallet: userWallet,
      status: "pending",
      timestamp: Date.now()
    })
    setEurAmount("")
    alert("Deposit request sent.")
  }

  return (
    <div className="deposit-page">
      <div className="deposit-form">
        <h2>Deposit Funds</h2>

        <label>Coin</label>
        <select value={coin} onChange={e => {
          const selected = e.target.value as Coin
          setCoin(selected)
          setNetwork(COIN_CONFIG[selected].networks[0])
        }}>
          {Object.keys(COIN_CONFIG).map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <label>Network</label>
        <select value={network} onChange={e => setNetwork(e.target.value)}>
          {COIN_CONFIG[coin].networks.map((n: string) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>

        <label>Amount (EUR)</label>
        <input
          type="number"
          placeholder="0.00"
          value={eurAmount}
          onChange={e => setEurAmount(e.target.value)}
        />

        <div className="conversion">
          You will receive: <strong>{coinAmount} {coin}</strong>
        </div>

        {userWallet ? (
          <>
            <div className="wallet-address">
              <strong>Deposit Address:</strong>
              <div className="address-box">{userWallet}</div>
              {qrUrl && <img src={qrUrl} alt="QR" />}
            </div>
            <button onClick={handleSubmit}>Proceed</button>
          </>
        ) : (
          <button onClick={handleActivateWallet}>
            Activate Wallet
          </button>
        )}
      </div>

      <div className="deposit-history">
        <h3>Deposit History</h3>
        <table>
          <thead>
            <tr>
              <th>Coin</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
  {deposits.map(d => {
    const info = coinIcons[d.coin] || {}
    return (
      <tr key={d.id}>
        <td>
          <div className="coin-info">
            <img src={info.image || "/default-coin.png"} alt={d.coin} />
            <div>
              <strong>{info.name || d.coin}</strong>
              <div className="symbol">{d.coin}</div>
            </div>
          </div>
        </td>
        <td>{d.coinAmount}</td>
        <td>{d.status}</td>
        <td>{new Date(d.timestamp).toLocaleString()}</td>
      </tr>
    )
  })}
</tbody>

        </table>
      </div>
    </div>
  )
}

export default Deposit
